import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterButton from "../../components/FilterButton";
import PlanTrip from "../../components/PlanTrip";
import PreTrip from "../../components/PreTrip";
import TourGuide from "../../components/TourGuide";
import {
  setActiveTab,
  setPlanTrips,
  setPreTrips,
} from "../../redux/trip/tripSlice";
import apiService from "../../services/api/apiServices";
import { LoaderCircle } from "lucide-react";

const Home = () => {
  const { activeTab } = useSelector((state) => state.trip);
  const { preTrips, planTrips } = useSelector((state) => state.trip);
  const { city } = useSelector((state) => state.onboarding);
  const { selectedFilters, categories } = useSelector((state) => state.filter);
  const { contentData } = useSelector((state) => state.content);

  // Separate loading states
  const [preTripLoading, setPreTripLoading] = useState(false);
  const [planTripLoading, setPlanTripLoading] = useState(false);
  const [preTripError, setPreTripError] = useState(null);
  const [planTripError, setPlanTripError] = useState(null);
  // Local state for filtered pre-trips
  const [filteredPreTrips, setFilteredPreTrips] = useState(preTrips);

  const dispatch = useDispatch();

  const cityName = city?.cityName;
  const languageCode = selectedFilters?.Languages?.code;
  const travelingTimeCode = selectedFilters?.["Traveling Time"]?.code;
  const currentLocationCode = selectedFilters?.["Current Location"]?.code;
  const defaultPlaces = categories.map((obj) => obj.code)?.join(",");

  // Fetch PreTrip data (only once)
  const fetchPreTripData = useCallback(async () => {
    if (preTrips.length > 0) return; // Don't fetch if already loaded

    const preTripBody = {
      cityName,
      category: "Mix1",
      language: languageCode,
      hours: String(travelingTimeCode),
      currentTimeMin: String(8 * 60),
      currentLocation: currentLocationCode,
    };

    setPreTripLoading(true);
    setPreTripError(null);

    try {
      const preTripsResponse = await apiService.getPreTrips(preTripBody);
      dispatch(setPreTrips(preTripsResponse || []));
    } catch (err) {
      setPreTripError("Failed to fetch Pre Trips. Please try again.");
    } finally {
      setPreTripLoading(false);
    }
  }, [
    cityName,
    currentLocationCode,
    dispatch,
    languageCode,
    preTrips,
    travelingTimeCode,
  ]);

  // Fetch PlanTrip data (every time filters change)
  const fetchPlanTripData = useCallback(async () => {
    const planTripBody = {
      cityName,
      category: selectedFilters.Places.length
        ? selectedFilters.Places.map((obj) => obj.code)?.join(",")
        : defaultPlaces,
      language: languageCode,
    };

    setPlanTripLoading(true);
    setPlanTripError(null);

    try {
      const planTripsResponse = await apiService.getPlanTrips(planTripBody);
      dispatch(setPlanTrips(planTripsResponse || []));
    } catch (err) {
      setPlanTripError("Failed to fetch Plan Trips. Please try again.");
    } finally {
      setPlanTripLoading(false);
    }
  }, [
    cityName,
    defaultPlaces,
    dispatch,
    languageCode,
    selectedFilters,
  ]);

  // Filter preTrips locally without modifying Redux state
  const fetchPreTripLocal = useCallback(() => {
    // Filter preTrips based on startLocation and tripDuration
    const filtered = preTrips.filter((trip) => {
      return (
        trip.startLocation === currentLocationCode &&
        String(trip.tripDuration) === String(travelingTimeCode)
      );
    });

    // Update local state with filtered data
    setFilteredPreTrips(filtered);
  }, [currentLocationCode, preTrips, travelingTimeCode]);

  useEffect(() => {
    fetchPreTripData();
  }, [fetchPreTripData]);

  useEffect(() => {
    fetchPlanTripData();
    fetchPreTripLocal();
  }, [fetchPlanTripData, fetchPreTripLocal]);

  // Update filteredPreTrips when preTrips changes (e.g., after initial fetch)
  useEffect(() => {
    // setFilteredPreTrips(preTrips);
    fetchPreTripLocal();
  }, [fetchPreTripLocal]);

  const renderPreTrip = () => {
    if (preTripLoading) {
      return (
        <div className="h-full w-full flex justify-center items-center">
          <div className="flex flex-col items-center justify-center">
            <LoaderCircle
              className="animate-spin ml-1.5 mt-[1px]"
              size={40}
              color={"#ED5722"}
              strokeWidth={3}
            />
            <span className="mt-1">Please wait while trips are loading!</span>
          </div>
        </div>
      );
    }

    if (preTripError) {
      return (
        <>
          <div className="p-3 text-red-500">{preTripError}</div>
          <FilterButton />
        </>
      );
    }

    if (filteredPreTrips.length === 0) {
      return (
        <>
          <div className="p-3">No Pre Trip data available</div>
          <FilterButton />
        </>
      );
    }

    return <PreTrip preTrips={filteredPreTrips} />;
  };

  const renderPlanTrip = () => {
    if (planTripLoading) {
      return (
        <div className="h-full w-full flex justify-center items-center">
          <LoaderCircle
            className="animate-spin ml-1.5 mt-[1px]"
            size={40}
            color={"#ED5722"}
            strokeWidth={3}
          />
        </div>
      );
    }

    if (planTripError) {
      return (
        <>
          <div className="p-3 text-red-500">{planTripError}</div>
          <FilterButton />
        </>
      );
    }

    if (planTrips.length === 0) {
      return (
        <>
          <div className="p-3">No Plan Trip data available</div>
          <FilterButton />
        </>
      );
    }

    return <PlanTrip />;
  };

  return (
    <div>
      {/* Modern Mobile App Hero Section */}
      <div className="relative bg-gradient-to-br from-orange-50 via-white to-orange-50 py-12 px-4 overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange1 opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-orange1 opacity-5 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Text Content */}
            <div className="flex-1 text-center md:text-left space-y-6">
              <div className="space-y-3">
                <h1 className="text-4xl md:text-5xl font-bold text-black1 leading-tight">
                  Discover Your Perfect
                  <span className="block text-orange1">Journey</span>
                </h1>
                <p className="text-lg text-gray-600 max-w-md mx-auto md:mx-0">
                  Plan amazing trips with our AI-powered travel companion. Download now and start exploring!
                </p>
              </div>
              
              {/* Play Store & Download Buttons */}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a
                  href="https://play.google.com/store/apps/details?id=com.aao.ghumen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-black1 hover:bg-gray-800 text-white px-6 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-xs text-gray-300">GET IT ON</div>
                    <div className="text-lg font-semibold -mt-0.5">Google Play</div>
                  </div>
                </a>

                <a
                  href="/1.jpg"
                  download
                  className="inline-flex items-center gap-2 bg-white text-black1 border border-gray-200 px-6 py-3.5 rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <svg
                    className="w-6 h-6 text-orange1"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M5 20h14v-2H5zm7-18l-5 5h3v6h4v-6h3z" />
                  </svg>
                  <span className="font-semibold">Download App Preview</span>
                </a>
              </div>

              {/* Stats/Features */}
              <div className="flex flex-wrap gap-6 justify-center md:justify-start pt-4">
                <div className="text-center md:text-left">
                  <div className="text-2xl font-bold text-orange1">50K+</div>
                  <div className="text-sm text-gray-600">Downloads</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-2xl font-bold text-orange1">4.5★</div>
                  <div className="text-sm text-gray-600">Rating</div>
                </div>
                <div className="text-center md:text-left">
                  <div className="text-2xl font-bold text-orange1">100+</div>
                  <div className="text-sm text-gray-600">Destinations</div>
                </div>
              </div>
            </div>

            {/* Mobile Mockup */}
            <div className="flex-1 flex justify-center items-center">
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-orange1 opacity-20 blur-3xl rounded-full scale-110"></div>
                
                {/* Phone Image */}
                <div className="relative transform hover:scale-105 transition-transform duration-500">
                  <img
                    src="/1.jpg"
                    alt="Aao Ghumen App"
                    className="w-64 md:w-80 h-auto drop-shadow-2xl rounded-3xl"
                  />
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-lg p-3 animate-bounce">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-semibold text-black1">Live Trips</span>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg p-3 animate-pulse">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🗺️</span>
                    <span className="text-xs font-semibold text-black1">Smart Routes</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Original Content */}
      {preTrips.length > 0 && <TourGuide />}
      <div className="flex px-3 py-4 capitalize">
        <button
          onClick={() => dispatch(setActiveTab("preTrip"))}
          className={`flex-1 py-1.5 text-center text-[15px] font-semibold ${
            activeTab === "preTrip"
              ? "bg-orange1 text-white rounded-md"
              : "text-black1"
          }`}
        >
          {contentData?.preTripPlan || ""}
        </button>
        <button
          onClick={() => dispatch(setActiveTab("planTrip"))}
          className={`plan-trip-tab flex-1 py-1.5 text-center text-[15px] font-semibold ${
            activeTab === "planTrip"
              ? "bg-orange1 text-white rounded-md"
              : "text-black1"
          }`}
        >
          {contentData?.planYourTrip || ""}
        </button>
      </div>
      <hr color="#B3B8D6" />
      <div className="h-[calc(100vh-184px)] overflow-auto relative">
        {activeTab === "preTrip" && renderPreTrip()}
        {activeTab === "planTrip" && renderPlanTrip()}
      </div>
    </div>
  );
};

export default Home;
